// 設備頁外框（SPEC #vms-web-220 選單表「設備」、#vms-web-327「新增 ▾」，VMS UX 評審 2026-10-13 P0-4）：
//   頁內分頁「全部設備｜韌體（/devices/firmware）｜庫存與開通（/devices/inventory：未歸戶｜偵測到的｜Kinio Node 開通）」，各自照權限出現；
//   右上只有一顆〔新增 ▾〕：新增 Kinio 設備／新增 ONVIF 攝影機／開通 Kinio Node／用序號認領（各項照權限出現）。
//   只有開通權（node_enroll）、沒有任何設備的人：直接到開通分頁、不顯示其他分頁。
import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScope } from '../../lib/scope';
import { hasOrgPerm, orgUsable } from '../../lib/planeApi';
import { orgSitesOrg } from './gatewayAccess';
import { useNodeEnrollOrgs } from '../inventory/useNodeEnrollOrgs';
import { OnvifDialog } from './OnvifDialog';
import { KinioChannelDialog } from './KinioChannelDialog';
import { PairDialog } from './PairDialog';

export function useDeviceTabs() {
  const { devices, orgs, homes } = useScope();
  const enrollOrgs = useNodeEnrollOrgs(orgs);
  const inventoryOrgs = orgs.filter((o) => o.kind === 'team' && orgUsable(o) && hasOrgPerm(o, 'org.devices'));
  const anyDevice = devices.some((d) => d.actions.some((a) => a.action === 'device.read')) || homes.length > 0;
  return {
    all: anyDevice || inventoryOrgs.length > 0,
    inventory: inventoryOrgs.length > 0 || enrollOrgs.length > 0,
    enroll: enrollOrgs.length > 0,
    claim: inventoryOrgs.length > 0,
    add: homes.some((h) => h.can_manage || h.role === 'owner' || h.role === 'admin') || inventoryOrgs.length > 0,
    // 閘道通道（ONVIF / Kinio 攝影機通道）：家戶 owner / admin，或企業家戶的 owner / org_admin / org.sites（同平台 onvifScope）
    channels: homes.some((h) => h.can_manage || h.role === 'owner' || h.role === 'admin' || (h.org_kind === 'team' && orgSitesOrg(orgs.find((o) => o.org_id === h.org_id)))),
  };
}

export function DevicesLayout() {
  const { t } = useTranslation();
  const loc = useLocation();
  const tabs = useDeviceTabs();
  const onInventory = loc.pathname.startsWith('/devices/inventory');
  const onEnroll = loc.pathname.startsWith('/devices/inventory/node-enroll');
  // 只有 Kinio Node 開通權的人（沒有設備、也沒有庫存權）：直接到開通分頁
  if (!tabs.all && !tabs.claim && tabs.enroll && !onInventory) return <Navigate to="/devices/inventory/node-enroll" replace />;
  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">{t('nav.devices')}</h1>
        <span className="spacer" />
        <AddMenu tabs={tabs} />
      </div>
      {(tabs.all || tabs.claim) && (
        <div className="tabs" role="tablist" aria-label={t('nav.devices')}>
          <NavLink to="/devices" end role="tab" data-testid="devtab-all">
            {t('devtabs.all')}
          </NavLink>
          <NavLink to="/devices/firmware" role="tab" data-testid="devtab-firmware">
            {t('devtabs.firmware')}
          </NavLink>
          {/* 庫存與開通攤平成兩個分頁（第二輪 R2-P2-3：不再「設備 › 庫存與開通 › Kinio Node 開通」三層） */}
          {tabs.claim && (
            <NavLink to="/devices/inventory" role="tab" data-testid="devtab-inventory" className={({ isActive }) => (isActive && !onEnroll ? 'active' : '')}>
              {t('devtabs.inventory')}
            </NavLink>
          )}
          {tabs.enroll && (
            <NavLink to="/devices/inventory/node-enroll" role="tab" data-testid="devtab-enroll">
              {t('devtabs.enroll')}
            </NavLink>
          )}
        </div>
      )}
      <Outlet />
    </div>
  );
}

function AddMenu({ tabs }: { tabs: ReturnType<typeof useDeviceTabs> }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pair, setPair] = useState(false);
  const [onvif, setOnvif] = useState(false);
  const [kinioCh, setKinioCh] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const items = [
    tabs.add && { key: 'kinio', label: t('devtabs.add_kinio'), run: () => setPair(true) },
    tabs.channels && { key: 'onvif', label: t('devtabs.add_onvif'), run: () => setOnvif(true) },
    tabs.channels && { key: 'kinio-channel', label: t('devtabs.add_kinio_channel'), run: () => setKinioCh(true) },
    tabs.enroll && { key: 'enroll', label: t('devtabs.add_enroll'), run: () => nav('/devices/inventory/node-enroll') },
    tabs.claim && { key: 'claim', label: t('devtabs.add_claim'), run: () => nav('/devices/inventory?claim=1') },
  ].filter(Boolean) as { key: string; label: string; run(): void }[];
  if (!items.length) return null;
  return (
    <div className="add-menu" ref={ref}>
      <button className="btn btn-primary" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="dev-add">
        {t('devtabs.add')} ▾
      </button>
      {open && (
        <div className="add-menu-panel card" role="menu">
          {items.map((it) => (
            <button
              key={it.key}
              role="menuitem"
              data-testid={'dev-add-' + it.key}
              onClick={() => {
                setOpen(false);
                it.run();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
      <OnvifDialog open={onvif} close={() => setOnvif(false)} />
      <KinioChannelDialog open={kinioCh} close={() => setKinioCh(false)} />
      <PairDialog open={pair} close={() => setPair(false)} />
    </div>
  );
}
